import React, { useState } from 'react';
import { supabase } from '../../../lib/supabase';
import { useAuth } from '../../../contexts/AuthContext';
import Icon from '../../../components/AppIcon';

const MOCK_SESSIONS = [
  { id: '1', device: 'MacBook Pro', browser: 'Chrome 122', location: 'New York, US', lastActive: '2 minutes ago', current: true },
  { id: '2', device: 'iPhone 15', browser: 'Safari Mobile', location: 'New York, US', lastActive: '1 hour ago', current: false },
  { id: '3', device: 'Windows PC', browser: 'Firefox 124', location: 'Chicago, US', lastActive: '2 days ago', current: false },
];

const MOCK_SECURITY_LOG = [
  { id: '1', timestamp: '2026-03-06 11:42', device: 'MacBook Pro / Chrome', status: 'Success', ip: '192.168.1.1' },
  { id: '2', timestamp: '2026-03-05 09:15', device: 'iPhone 15 / Safari', status: 'Success', ip: '192.168.1.2' },
  { id: '3', timestamp: '2026-03-04 22:30', device: 'Unknown / Firefox', status: 'Failed', ip: '45.33.32.156' },
  { id: '4', timestamp: '2026-03-03 14:20', device: 'MacBook Pro / Chrome', status: 'Success', ip: '192.168.1.1' },
  { id: '5', timestamp: '2026-03-02 08:05', device: 'Windows PC / Edge', status: 'Success', ip: '10.0.0.5' },
];

function PasswordStrength({ password }) {
  const getStrength = (p) => {
    if (!p) return { level: 0, label: '', color: '' };
    let score = 0;
    if (p?.length >= 8) score++;
    if (/[A-Z]/?.test(p)) score++;
    if (/[0-9]/?.test(p)) score++;
    if (/[^A-Za-z0-9]/?.test(p)) score++;
    if (score <= 1) return { level: 1, label: 'Weak', color: '#EF4444' };
    if (score <= 2) return { level: 2, label: 'Medium', color: '#F59E0B' };
    return { level: 3, label: 'Strong', color: '#22C55E' };
  };
  const { level, label, color } = getStrength(password);
  if (!password) return null;
  return (
    <div className="mt-2">
      <div className="flex gap-1 mb-1">
        {[1, 2, 3]?.map(i => (
          <div key={i} className="h-1 flex-1 rounded-full transition-all duration-300" style={{ background: i <= level ? color : 'rgba(255,255,255,0.1)' }} />
        ))}
      </div>
      <span className="text-xs" style={{ color }}>{label}</span>
    </div>
  );
}

export default function SecurityTab({ onToast }) {
  const { user, signInWithGoogle } = useAuth();
  const [pwForm, setPwForm] = useState({ current: '', newPw: '', confirm: '' });
  const [pwSaving, setPwSaving] = useState(false);
  const [mfaEnabled, setMfaEnabled] = useState(false);
  const [showMfaModal, setShowMfaModal] = useState(false);
  const [mfaCode, setMfaCode] = useState('');
  const [sessions, setSessions] = useState(MOCK_SESSIONS);
  const [revokingId, setRevokingId] = useState(null);

  const googleConnected = user?.app_metadata?.providers?.includes('google') || false;

  const handlePasswordChange = async () => {
    if (!pwForm?.newPw || !pwForm?.confirm) return onToast?.({ type: 'error', message: 'Please fill all fields' });
    if (pwForm?.newPw !== pwForm?.confirm) return onToast?.({ type: 'error', message: 'Passwords do not match' });
    if (pwForm?.newPw?.length < 8) return onToast?.({ type: 'error', message: 'Password must be at least 8 characters' });
    setPwSaving(true);
    try {
      const { error } = await supabase?.auth?.updateUser({ password: pwForm?.newPw });
      if (error) throw error;
      setPwForm({ current: '', newPw: '', confirm: '' });
      onToast?.({ type: 'success', message: 'Password updated successfully!' });
    } catch (err) {
      onToast?.({ type: 'error', message: err?.message || 'Failed to update password' });
    } finally {
      setPwSaving(false);
    }
  };

  const handleRevokeSession = async (sessionId) => {
    setRevokingId(sessionId);
    try {
      await new Promise(r => setTimeout(r, 800));
      setSessions(prev => prev?.filter(s => s?.id !== sessionId));
      onToast?.({ type: 'success', message: 'Session revoked' });
    } catch (err) {
      onToast?.({ type: 'error', message: 'Failed to revoke session' });
    } finally {
      setRevokingId(null);
    }
  };

  const handleMfaVerify = () => {
    if (mfaCode?.length === 6) {
      setMfaEnabled(true);
      setShowMfaModal(false);
      setMfaCode('');
      onToast?.({ type: 'success', message: 'Two-factor authentication enabled!' });
    } else {
      onToast?.({ type: 'error', message: 'Enter a valid 6-digit code' });
    }
  };

  const inputStyle = { background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(0,229,255,0.15)' };

  return (
    <div className="space-y-8">
      {/* Change Password */}
      <div>
        <h3 className="text-base font-semibold text-white mb-4 flex items-center gap-2">
          <Icon name="KeyRound" size={16} color="#00E5FF" />
          Change Password
        </h3>
        <div className="space-y-4 p-5 rounded-2xl" style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(0,229,255,0.1)' }}>
          {[
            { key: 'current', label: 'Current Password', placeholder: '••••••••' },
            { key: 'newPw', label: 'New Password', placeholder: 'Min. 8 characters' },
            { key: 'confirm', label: 'Confirm New Password', placeholder: 'Repeat new password' },
          ]?.map(({ key, label, placeholder }) => (
            <div key={key}>
              <label className="block text-sm font-medium mb-2" style={{ color: '#94A3B8' }}>{label}</label>
              <input
                type="password"
                value={pwForm?.[key] || ''}
                placeholder={placeholder}
                onChange={e => setPwForm(prev => ({ ...prev, [key]: e?.target?.value }))}
                className="w-full px-4 py-3 rounded-xl text-sm text-white placeholder-[#475569] outline-none focus:ring-2 focus:ring-[#00E5FF] transition-all"
                style={inputStyle}
              />
              {key === 'newPw' && <PasswordStrength password={pwForm?.newPw} />}
            </div>
          ))}
          <button
            onClick={handlePasswordChange}
            disabled={pwSaving}
            className="flex items-center gap-2 px-5 py-2.5 rounded-xl text-sm font-semibold transition-all hover:scale-[1.02] disabled:opacity-50"
            style={{ background: 'linear-gradient(135deg, #00E5FF, #0891B2)', color: '#0D1B2A' }}
          >
            {pwSaving ? <div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin" /> : <Icon name="Lock" size={14} color="currentColor" />}
            {pwSaving ? 'Updating...' : 'Update Password'}
          </button>
        </div>
      </div>
      {/* Two-Factor Authentication */}
      <div>
        <h3 className="text-base font-semibold text-white mb-4 flex items-center gap-2">
          <Icon name="Smartphone" size={16} color="#00E5FF" />
          Two-Factor Authentication
        </h3>
        <div className="flex items-center justify-between p-4 rounded-xl" style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.06)' }}>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: mfaEnabled ? 'rgba(34,197,94,0.15)' : 'rgba(255,255,255,0.06)' }}>
              <Icon name={mfaEnabled ? 'ShieldCheck' : 'Shield'} size={18} color={mfaEnabled ? '#22C55E' : '#64748B'} />
            </div>
            <div>
              <p className="text-sm font-medium text-white">Authenticator App</p>
              <p className="text-xs" style={{ color: mfaEnabled ? '#22C55E' : '#64748B' }}>{mfaEnabled ? 'Enabled — Your account is protected' : 'Not enabled — Add extra security'}</p>
            </div>
          </div>
          <button
            onClick={() => mfaEnabled ? (setMfaEnabled(false), onToast?.({ type: 'success', message: 'MFA disabled' })) : setShowMfaModal(true)}
            className="px-4 py-2 rounded-lg text-xs font-semibold transition-all hover:scale-[1.02]"
            style={mfaEnabled ? { background: 'rgba(239,68,68,0.15)', color: '#EF4444', border: '1px solid rgba(239,68,68,0.3)' } : { background: 'rgba(0,229,255,0.12)', color: '#00E5FF', border: '1px solid rgba(0,229,255,0.3)' }}
          >
            {mfaEnabled ? 'Disable MFA' : 'Enable MFA'}
          </button>
        </div>
      </div>
      {/* Active Sessions */}
      <div>
        <h3 className="text-base font-semibold text-white mb-4 flex items-center gap-2">
          <Icon name="Monitor" size={16} color="#00E5FF" />
          Active Sessions
        </h3>
        <div className="space-y-3">
          {sessions?.map(session => (
            <div key={session?.id} className="flex items-center justify-between p-4 rounded-xl" style={{ background: 'rgba(255,255,255,0.04)', border: `1px solid ${session?.current ? 'rgba(0,229,255,0.2)' : 'rgba(255,255,255,0.06)'}` }}>
              <div className="flex items-center gap-3">
                <Icon name={session?.device?.includes('iPhone') ? 'Smartphone' : 'Monitor'} size={16} color={session?.current ? '#00E5FF' : '#64748B'} />
                <div>
                  <div className="flex items-center gap-2">
                    <p className="text-sm font-medium text-white">{session?.device}</p>
                    {session?.current && <span className="text-xs px-2 py-0.5 rounded-full" style={{ background: 'rgba(0,229,255,0.15)', color: '#00E5FF' }}>Current</span>}
                  </div>
                  <p className="text-xs" style={{ color: '#64748B' }}>{session?.browser} · {session?.location} · {session?.lastActive}</p>
                </div>
              </div>
              {!session?.current && (
                <button
                  onClick={() => handleRevokeSession(session?.id)}
                  disabled={revokingId === session?.id}
                  className="px-3 py-1.5 rounded-lg text-xs font-medium transition-all hover:scale-[1.02] disabled:opacity-50"
                  style={{ background: 'rgba(239,68,68,0.12)', color: '#EF4444', border: '1px solid rgba(239,68,68,0.25)' }}
                >
                  {revokingId === session?.id ? '...' : 'Revoke'}
                </button>
              )}
            </div>
          ))}
        </div>
      </div>
      {/* Connected Accounts */}
      <div>
        <h3 className="text-base font-semibold text-white mb-4 flex items-center gap-2">
          <Icon name="Link" size={16} color="#00E5FF" />
          Connected Accounts
        </h3>
        <div className="flex items-center justify-between p-4 rounded-xl" style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.06)' }}>
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-lg flex items-center justify-center" style={{ background: 'rgba(255,255,255,0.08)' }}>
              <span className="text-sm font-bold" style={{ color: '#4285F4' }}>G</span>
            </div>
            <div>
              <p className="text-sm font-medium text-white">Google</p>
              <p className="text-xs" style={{ color: googleConnected ? '#22C55E' : '#64748B' }}>{googleConnected ? 'Connected' : 'Not connected'}</p>
            </div>
          </div>
          <button
            onClick={() => !googleConnected && signInWithGoogle?.()}
            className="px-4 py-2 rounded-lg text-xs font-semibold transition-all hover:scale-[1.02]"
            style={googleConnected ? { background: 'rgba(239,68,68,0.12)', color: '#EF4444', border: '1px solid rgba(239,68,68,0.25)' } : { background: 'rgba(0,229,255,0.12)', color: '#00E5FF', border: '1px solid rgba(0,229,255,0.3)' }}
          >
            {googleConnected ? 'Disconnect' : 'Connect'}
          </button>
        </div>
      </div>
      {/* Security Log */}
      <div>
        <h3 className="text-base font-semibold text-white mb-4 flex items-center gap-2">
          <Icon name="ClipboardList" size={16} color="#00E5FF" />
          Recent Login Activity
        </h3>
        <div className="rounded-xl overflow-hidden" style={{ border: '1px solid rgba(255,255,255,0.06)' }}>
          <table className="w-full text-sm">
            <thead>
              <tr style={{ background: 'rgba(255,255,255,0.04)' }}>
                {['Timestamp', 'Device / Browser', 'Status']?.map(h => (
                  <th key={h} className="px-4 py-3 text-left text-xs font-medium" style={{ color: '#64748B' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {MOCK_SECURITY_LOG?.map((log, i) => (
                <tr key={log?.id} style={{ background: i % 2 === 0 ? 'transparent' : 'rgba(255,255,255,0.02)', borderTop: '1px solid rgba(255,255,255,0.04)' }}>
                  <td className="px-4 py-3 text-xs" style={{ color: '#94A3B8' }}>{log?.timestamp}</td>
                  <td className="px-4 py-3 text-xs" style={{ color: '#94A3B8' }}>{log?.device}</td>
                  <td className="px-4 py-3">
                    <span className="text-xs px-2 py-0.5 rounded-full" style={log?.status === 'Success' ? { background: 'rgba(34,197,94,0.15)', color: '#22C55E' } : { background: 'rgba(239,68,68,0.15)', color: '#EF4444' }}>
                      {log?.status}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      {/* MFA Setup Modal */}
      {showMfaModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: 'rgba(0,0,0,0.7)', backdropFilter: 'blur(8px)' }}>
          <div className="w-full max-w-sm p-6 rounded-2xl" style={{ background: '#0D1B2A', border: '1px solid rgba(0,229,255,0.2)' }}>
            <h3 className="text-lg font-semibold text-white mb-2">Enable Two-Factor Auth</h3>
            <p className="text-sm mb-5" style={{ color: '#94A3B8' }}>Scan the QR code with your authenticator app, then enter the 6-digit code.</p>
            <div className="w-40 h-40 mx-auto mb-5 rounded-xl flex items-center justify-center" style={{ background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(0,229,255,0.15)' }}>
              <div className="text-center">
                <Icon name="QrCode" size={48} color="#00E5FF" />
                <p className="text-xs mt-2" style={{ color: '#64748B' }}>QR Code Placeholder</p>
              </div>
            </div>
            <input
              type="text"
              maxLength={6}
              value={mfaCode}
              onChange={e => setMfaCode(e?.target?.value?.replace(/\D/g, ''))}
              placeholder="000000"
              className="w-full px-4 py-3 rounded-xl text-center text-lg font-mono text-white placeholder-[#475569] outline-none focus:ring-2 focus:ring-[#00E5FF] mb-4"
              style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(0,229,255,0.15)', letterSpacing: '0.3em' }}
            />
            <div className="flex gap-3">
              <button onClick={() => setShowMfaModal(false)} className="flex-1 py-2.5 rounded-xl text-sm" style={{ background: 'rgba(255,255,255,0.06)', color: '#94A3B8', border: '1px solid rgba(255,255,255,0.08)' }}>Cancel</button>
              <button onClick={handleMfaVerify} className="flex-1 py-2.5 rounded-xl text-sm font-semibold" style={{ background: 'linear-gradient(135deg, #00E5FF, #0891B2)', color: '#0D1B2A' }}>Verify & Enable</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
